import { useState, useCallback } from 'react';
import { CardinalDirection, MaintenanceIntervalType } from '../../../../types/Enums';

/**
 * Interval settings modal context - tracks which activity's interval is being edited
 */
export interface IntervalSettingsContext {
    componentKey: string;
    /** Every component the interval is written to (Massredigering: the selected ones with the row) */
    componentKeys: string[];
    templateId: string;
    templateName: string;
    templateIntervalValue: number;
    templateIntervalType: MaintenanceIntervalType;
    /** The component's direction, own or inherited from a parent */
    cardinalDirection: CardinalDirection | null;
    /** The template interval is the template's own interval for `cardinalDirection` */
    hasDirectionalInterval: boolean;
    currentIntervalValue: number;
    currentIntervalType: MaintenanceIntervalType;
    isCustomInterval: boolean;
}

interface UseIntervalModalProps {
    handleMaintenanceIntervalChange: (componentKey: string, templateId: string, value: string) => void;
    handleMaintenanceIntervalBlur: (componentKey: string, templateId: string) => void;
    handleMaintenanceIntervalTypeChange: (componentKey: string, templateId: string, type: MaintenanceIntervalType) => void;
}

interface UseIntervalModalReturn {
    showIntervalModal: boolean;
    intervalContext: IntervalSettingsContext | null;
    handleOpenIntervalModal: (context: IntervalSettingsContext) => void;
    handleCloseIntervalModal: () => void;
    handleSaveInterval: (
        intervalValue: number,
        intervalType: MaintenanceIntervalType,
        useTemplate: boolean
    ) => void;
}

/**
 * Hook for managing interval settings modal.
 * Handles configuring custom intervals or restoring to template default.
 */
export const useIntervalModal = ({
    handleMaintenanceIntervalChange,
    handleMaintenanceIntervalBlur,
    handleMaintenanceIntervalTypeChange
}: UseIntervalModalProps): UseIntervalModalReturn => {
    const [showIntervalModal, setShowIntervalModal] = useState<boolean>(false);
    const [intervalContext, setIntervalContext] = useState<IntervalSettingsContext | null>(null);

    const handleOpenIntervalModal = useCallback((context: IntervalSettingsContext): void => {
        setIntervalContext(context);
        setShowIntervalModal(true);
    }, []);

    const handleCloseIntervalModal = useCallback((): void => {
        setShowIntervalModal(false);
        setIntervalContext(null);
    }, []);

    const handleSaveInterval = useCallback((
        intervalValue: number,
        intervalType: MaintenanceIntervalType,
        useTemplate: boolean
    ): void => {
        if (!intervalContext) return;

        const { componentKeys, templateId, templateIntervalValue, templateIntervalType } = intervalContext;

        for (const componentKey of componentKeys) {
            if (useTemplate) {
                // Restore to template default
                handleMaintenanceIntervalChange(componentKey, templateId, templateIntervalValue.toString());
                handleMaintenanceIntervalTypeChange(componentKey, templateId, templateIntervalType);
            } else {
                // Use custom interval
                handleMaintenanceIntervalChange(componentKey, templateId, intervalValue.toString());
                handleMaintenanceIntervalTypeChange(componentKey, templateId, intervalType);
            }

            // Trigger blur to autosave
            handleMaintenanceIntervalBlur(componentKey, templateId);
        }

        handleCloseIntervalModal();
    }, [
        intervalContext,
        handleMaintenanceIntervalChange,
        handleMaintenanceIntervalTypeChange,
        handleMaintenanceIntervalBlur,
        handleCloseIntervalModal
    ]);

    return {
        showIntervalModal,
        intervalContext,
        handleOpenIntervalModal,
        handleCloseIntervalModal,
        handleSaveInterval
    };
};
